import React, { useEffect, useRef, useState } from "react";
import mainStyle from "../../dashboard.module.scss";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, FormControl, Select, MenuItem } from "@mui/material";
import * as echarts from "echarts";
import { mockEventTrendsDates, mockEventTrendsDataset } from "../../const";
import icn_line_chart from "assets/images/icons/icn_line_chart.svg";

const DetectedEventTrends = ({ sortable, dataset = mockEventTrendsDataset }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [selectedDevice, setSelectedDevice] = useState("all");

  const dates = mockEventTrendsDates;
  const eventCounts = dataset[selectedDevice] || [];

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    const option = {
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        borderColor: "rgba(0, 0, 0, 0.08)",
        borderWidth: 1,
        shadowColor: "rgba(0, 0, 0, 0.08)",
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 2,
        textStyle: { color: "#333333" },
        formatter: (params) => {
          if (!params || params.length === 0) return "";
          return `<div style="font-size: 13px; font-family: Inter, sans-serif; padding: 4px 8px; color: #333333;">
                    <div style="font-weight: bold; margin-bottom: 8px; color: #111111;">Date: ${params[0].name}</div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="display:inline-block; width:10px; height:10px; border-radius:50%; background-color:${params[0].color}; vertical-align:middle;"></span>
                      Events: <strong style="color: #111111; margin-left: 4px;">${params[0].value}</strong>
                    </div>
                  </div>`;
        },
      },
      grid: {
        left: "5%",
        right: "5%",
        top: "12%",
        bottom: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates,
        axisLabel: {
          color: theme.palette.primary.greyFourth || "#888",
          fontSize: 11,
        },
        axisLine: {
          lineStyle: { color: theme.palette.primary.greyThread || "#DDD" },
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.primary.greyFourth || "#888",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.primary.greyThread || "#EEE",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Events",
          type: "line",
          smooth: true,
          showSymbol: false,
          data: eventCounts,
          lineStyle: {
            color: theme.palette.primary.main || "#1976D2",
            width: 2.5,
          },
          itemStyle: {
            color: theme.palette.primary.main || "#1976D2",
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: theme.palette.primary.mainGradientPrimary || "rgba(25, 118, 210, 0.3)" },
              { offset: 0.8, color: "rgba(25, 118, 210, 0.02)" },
              { offset: 1, color: "transparent" },
            ]),
          },
        },
      ],
    };

    chart.setOption(option);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartDom);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [theme, selectedDevice]);

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      className={`main-card-content ${mainStyle["dndObj_card"]} ${
        sortable === true ? mainStyle["dndObj_edit"] : ""
      }`}
    >
      <Box
        className={mainStyle["dndObj_title"]}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img src={icn_line_chart} alt="event_trends_icon" />
          <Typography component="span" sx={{ marginLeft: "10px" }}>
            Detected Event Trends
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" variant="outlined">
            <Select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              sx={{
                height: "28px",
                fontSize: "12px",
                borderRadius: "6px",
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: "12px" }}>All</MenuItem>
              <MenuItem value="HQ-NVR-01" sx={{ fontSize: "12px" }}>HQ-NVR-01</MenuItem>
              <MenuItem value="Retail-NVR-02" sx={{ fontSize: "12px" }}>Retail-NVR-02</MenuItem>
              <MenuItem value="Warehouse-NVR-03" sx={{ fontSize: "12px" }}>Warehouse-NVR-03</MenuItem>
              <MenuItem value="Campus-NVR-04" sx={{ fontSize: "12px" }}>Campus-NVR-04</MenuItem>
              <MenuItem value="Factory-NVR-05" sx={{ fontSize: "12px" }}>Factory-NVR-05</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, position: "relative", p: 1 }}>
        <Box ref={chartRef} sx={{ width: "100%", height: "190px" }} />
      </Box>
    </Box>
  );
};

export default DetectedEventTrends;
